import React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import { SxProps, Theme } from '@mui/material';
import { defaultTheme } from 'styles/theme/default';

export type NewButtonVariant = 'filled' | 'cancel' | 'outlined';

interface NewButtonProps extends Omit<ButtonProps, 'sx'> {
  variantType: NewButtonVariant;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

const buttonStyles: Record<NewButtonVariant, SxProps<Theme>> = {
  outlined: {
    borderRadius: 2,
    height: 40,
    px: 3,
    py: 1.5,
    border: `2px solid ${defaultTheme.colors.base}`,
    color: '#7ead20',
    background: '#fff',
    fontWeight: 600,
    transition: 'all 0.2s',
    '&:hover': {
      background: '#e6fabc',
      borderColor: '#7ead20',
      color: '#7ead20',
      border: `2px solid ${defaultTheme.colors.base}`,
    },
    boxShadow: 'none',
  },
  filled: {
    borderRadius: 2,
    height: 40,
    px: 3,
    py: 1.5,
    background: defaultTheme.colors.base,
    color: '#fff',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(39, 174, 96, 0.10)',
    transition: 'all 0.2s',
    '&:hover': {
      background: '#7ead20',
      color: '#fff',
      boxShadow: '0 2px 8px rgba(39, 174, 96, 0.10)',
    },
    '&:active': {
      background: '#7ead20',
      color: '#fff',
      boxShadow: '0 2px 8px rgba(39, 174, 96, 0.10)',
    },
  },
  cancel: {
    borderRadius: 2,
    height: 40,
    px: 3,
    py: 1.5,
    background: '#fff5f5',
    color: '#c0392b',
    border: '2px solid #f5c6cb',
    fontWeight: 600,
    transition: 'all 0.2s',
    '&:hover': {
      background: '#ffeaea',
      color: '#a93226',
      border: '2px solid',
      borderColor: '#f1948a',
    },
  },
};

const buttonVariants: Record<NewButtonVariant, ButtonProps['variant']> = {
  filled: 'contained',
  cancel: 'outlined',
  outlined: 'outlined',
};

const buttonColors: Record<NewButtonVariant, ButtonProps['color'] | undefined> = {
  filled: 'success',
  cancel: 'error',
  outlined: undefined,
};

export const NewButton = ({
  variantType,
  children,
  sx,
  startIcon,
  ...props
}: NewButtonProps): JSX.Element => {
  return (
    <Button
      variant={buttonVariants[variantType]}
      color={buttonColors[variantType]}
      sx={[buttonStyles[variantType], sx] as SxProps<Theme>}
      startIcon={startIcon}
      {...props}
    >
      {children}
    </Button>
  );
};

export default NewButton; 